
import VueApp from '@/@types/app/VueApp';
import { Component, Watch } from 'vue-property-decorator';
import {
  commitSetCommentsSidebarIsVisible,
  commitSetCommentsSidebarScrollToCommentId,
} from '@/store/commits/commentsSidebarCommits';
import { ArticleComment } from '@/models/article/ArticleComment';
import getTextNodeOffset from '@/components/tiptap/utils/getTextNodeOffset';

type RawIconData = { commentId: string; icon: string; top: number; isComment: boolean };
type CommentIcon = RawIconData & { left: number };

const ICON_SHIFT = 12;
const ICON_HEIGHT = 24;
const MAX_H_STACK = 2; // Max icons to stack horizontally before wrapping down

@Component({})
export default class TipTapCommentsIconsSidebar extends VueApp {
  protected containerSelector = '#article-text-editor';
  commentIcons: CommentIcon[] = [];

  // Timer reference for debouncing
  private updateTimeout: number | undefined;

  update() {
    const container = document.querySelector<HTMLElement>(this.containerSelector);

    if (!container) {
      this.commentIcons = [];
      return;
    }

    const activeTextTabId = this.getActiveTextTabId();
    const isFirstTextTab = this.isFirstTextTab(activeTextTabId);

    const comments = this.$store.state.commentsSidebar.comments.filter((comment) => {
      const hasMatchingTextTab = comment.textTabId ? comment.textTabId === activeTextTabId : isFirstTextTab;
      return comment?.quote?.paragraphKey && hasMatchingTextTab;
    });

    const rawIcons: RawIconData[] = [];

    comments.forEach((comment) => {
      // Skip if no icon definition
      if (!comment?.quote?.icon) return;

      // Skip if already processed in this batch
      if (rawIcons.find((i) => i.commentId === comment.id)) return;

      const element = container.querySelector<HTMLElement>('[key="' + comment.quote.paragraphKey + '"]');
      if (!element) return;

      // Create invisible clone to measure text position
      const elementClone = element.cloneNode(true) as HTMLElement;
      elementClone.style.visibility = 'hidden';
      elementClone.style.height = 'auto';
      element.parentNode.appendChild(elementClone);

      const textElement = elementClone.tagName === 'PRE' ? elementClone.firstElementChild : elementClone;
      const offsetTop = this.getRelativeOffsetTop(element, container);

      let textOffset = 0;
      if (comment.quote.paragraphText && comment.quote.paragraphText.length > 0) {
        const textIndex = this.getParagraphTextIndex(comment, element);
        const textNodeOffsetTop = getTextNodeOffset(textElement, textIndex, comment.quote.paragraphText.length).top;
        textOffset = textNodeOffsetTop - elementClone.getBoundingClientRect().top;
      }

      const isCommentIcon = comment?.quote?.icon === 'comment';
      const naturalTop = offsetTop + textOffset;

      // Clean up clone
      elementClone.parentNode.removeChild(elementClone);

      rawIcons.push({
        commentId: comment.id,
        top: naturalTop,
        icon: comment.quote?.icon || '',
        isComment: isCommentIcon,
      });
    });

    this.commentIcons = this.recalculateLayout(rawIcons);
  }

  recalculateLayout(icons: RawIconData[]): CommentIcon[] {
    // Sort by top position so we process top-down
    const sorted = icons.sort((a, b) => a.top - b.top);
    const placedIcons: CommentIcon[] = [];

    sorted.forEach((icon) => {
      let placed = false;
      let currentTop = icon.top;
      let currentStackLevel = 0; // 0 = closest to text, 1 = shifted right, etc.

      while (!placed) {
        const collision = placedIcons.find(
          (p) =>
            Math.abs(p.top - currentTop) < ICON_HEIGHT && // Vertical collision (within 24px)
            p.left === currentStackLevel * ICON_SHIFT // Horizontal collision
        );

        if (!collision) {
          placedIcons.push({
            ...icon,
            top: currentTop,
            left: currentStackLevel * ICON_SHIFT,
          });
          placed = true;
        } else {
          // Collision detected
          // Try moving Right
          currentStackLevel++;

          // If shifted too far right (Max Stack reached)
          if (currentStackLevel >= MAX_H_STACK) {
            // Reset horizontal shift
            currentStackLevel = 0;
            // Move down specifically to avoid the previous clutter
            currentTop += ICON_HEIGHT;
          }
        }
      }
    });

    return placedIcons;
  }

  getRelativeOffsetTop(element: HTMLElement, container: HTMLElement) {
    let offsetTop = 0;
    let node = element;
    while (node && node !== container) {
      offsetTop += node.offsetTop;
      node = node.offsetParent as HTMLElement;
    }
    return offsetTop;
  }

  getParagraphTextIndex(comment: ArticleComment, node: HTMLElement) {
    const baseIndex = node.textContent.indexOf(comment.quote.paragraphText);
    const isExists = baseIndex >= 0;
    let index = -1;

    if (isExists) {
      if (comment.quote.paragraphTextBefore && comment.quote.paragraphTextOffset) {
        index = node.textContent.indexOf(
          comment.quote.paragraphTextBefore + comment.quote.paragraphText,
          comment.quote.paragraphTextOffset - comment.quote.paragraphTextBefore.length
        );
        if (index >= 0) index += comment.quote.paragraphTextBefore.length;
      } else if (index < 0 && comment.quote.paragraphTextBefore) {
        index = node.textContent.indexOf(comment.quote.paragraphTextBefore + comment.quote.paragraphText, 0);
      }
    }
    if (index < 0) index = baseIndex;

    return index;
  }

  onClickComment(commentId: string) {
    commitSetCommentsSidebarScrollToCommentId(commentId);
    commitSetCommentsSidebarIsVisible(true);
  }

  getActiveTextTabId(): string | null {
    return this.$store.state.workspaceView.activeTextTabId || this.$store.state.articleEditor.activeTextTabId;
  }

  isFirstTextTab(activeTextTabId: string | null): boolean {
    const article = this.$store.state.workspaceView.selectedArticle || this.$store.state.articleEditor.article;
    if (!article || !article.textTabs || !article.textTabs.length) {
      return false;
    }
    const firstTextTabId = article.textTabs[0]?.id;
    return activeTextTabId === firstTextTabId;
  }

  mounted() {
    this.debouncedUpdate();
    window.addEventListener('resize', this.debouncedUpdate);
  }

  beforeDestroy() {
    window.removeEventListener('resize', this.debouncedUpdate);
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }
  }

  debouncedUpdate() {
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }

    this.updateTimeout = window.setTimeout(() => {
      this.$nextTick(() => {
        this.update();
      });
    }, 100);
  }

  @Watch('$store.state.commentsSidebar.comments')
  onCommentsChanged() {
    this.debouncedUpdate();
  }

  @Watch('$store.state.workspaceView.activeTextTabId')
  onTextTabChange() {
    this.debouncedUpdate();
  }

  @Watch('$store.state.articleEditor.activeTextTabId')
  onEditorTextTabChange() {
    this.debouncedUpdate();
  }

  @Watch('$store.state.workspaceView.selectedArticle')
  onSelectedArticleChanged() {
    this.commentIcons = [];
    this.debouncedUpdate();
  }
}
